import { useState, useContext, useRef, useEffect, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  alpha,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import LocalCafeIcon from '@mui/icons-material/LocalCafe';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import RamenDiningIcon from '@mui/icons-material/RamenDining';
import SetMealIcon from '@mui/icons-material/SetMeal';
import IcecreamIcon from '@mui/icons-material/Icecream';
import LocalPizzaIcon from '@mui/icons-material/LocalPizza';
import KebabDiningIcon from '@mui/icons-material/KebabDining';
import LunchDiningIcon from '@mui/icons-material/LunchDining';
import CategoryIcon from '@mui/icons-material/Category';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import StoreIcon from '@mui/icons-material/Store';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import Badge from '@mui/material/Badge';
import axiosInstance from '../../lib/axios';
import { useSnackbar } from '../../components/SnackbarProvider';
import { UpgradeModal } from '../../components/UpgradeModal';
import { useTierAccess } from '../../hooks/useTierAccess';
import { getUser, setUser } from '../../utils/auth';
import { ColorModeContext } from '../../contexts/ColorModeContext';
import { useAnimations } from '../../contexts/AnimationsContext';
import { OfflineContext } from '../../contexts/OfflineContext';
import { OfflineIndicator } from '../../components/OfflineIndicator';
import { Receipt } from '../../components/Receipt';
import { createRoot } from 'react-dom/client';
import { OrderType, OrderPaymentStatus, OrderFulfillmentStatus, FulfillmentMethod } from '../../types';
import type { Product, Location, ProductImage, Cart, BarcodeSearchResponse } from '../../types';
import { BarcodeScannerModal } from './components/BarcodeScannerModal';
import { SuspendCartModal } from './components/SuspendCartModal';
import { SuspendedCartsPanel } from './components/SuspendedCartsPanel';
import { LocationSelector } from './components/LocationSelector';
import { CustomerSelector } from './components/CustomerSelector';
import { QuickCustomerModal } from './components/QuickCustomerModal';
import { DiscountInput } from './components/DiscountInput';
import { ReturnLookupModal } from './components/ReturnLookupModal';
import { B2BModeToggle, SaleMode } from './components/B2BModeToggle';
import { ResellerSelector } from '../sales-b2b/components/ResellerSelector';
import { B2BPricingCalculator } from '../sales-b2b/components/B2BPricingCalculator';
import UndoIcon from '@mui/icons-material/Undo';
import BusinessIcon from '@mui/icons-material/Business';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import { saveProducts, saveInventory, getProducts, getInventory } from '../../lib/db';
import { ProductInventoryDialog } from '../../components/ProductInventoryDialog';
import type { ResellerProfile } from '../../types/b2b';
import { Customer } from '../../types/customer';
import { OpenShiftModal } from '../cash-drawer/OpenShiftModal';
import { CloseShiftModal } from '../cash-drawer/CloseShiftModal';
import type { CashDrawerShift } from '../../types/cashDrawer';
import { CashDrawerShiftStatus } from '../../types/cashDrawer';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LockIcon from '@mui/icons-material/Lock';
import Chip from '@mui/material/Chip';

// B2B Pricing types for POS integration
interface B2BPricingItemResult {
  productId: string;
  productName: string;
  quantity: number;
  retailPrice: number;
  b2bBasePrice: number;
  tierName: string | null;
  tierDiscount: number;
  tierDiscountAmount: number;
  quantityDiscount: number;
  quantityDiscountAmount: number;
  finalUnitPrice: number;
  subtotal: number;
}

interface B2BPricingSummary {
  subtotal: number;
  totalTierDiscount: number;
  totalQuantityDiscount: number;
  totalSavings: number;
  savingsPercentage: number;
  grandTotal: number;
}

interface B2BPricingResponse {
  items: B2BPricingItemResult[];
  summary: B2BPricingSummary;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Helper to construct full image URL from relative path
const getImageUrl = (image: ProductImage): string => {
  const path = image.thumbnail || image.medium || image.large;
  if (!path) return '';
  if (path.startsWith('http')) return path;
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  return `${API_URL}/${cleanPath}`;
};

interface CartItem {
  product: Product;
  quantity: number;
  price: number;
}

// Modern POS Color System with Pastel Categories
const posColors = {
  // Base colors
  bg: '#0D0D0D',
  surface: '#1A1A1A',
  surfaceLight: '#252525',
  surfaceElevated: '#2D2D2D',
  border: '#3A3A3A',

  // Text
  textPrimary: '#FFFFFF',
  textSecondary: '#B0B0B0',
  textMuted: '#6B6B6B',

  // Accent
  accent: '#8B5CF6',
  accentLight: '#A78BFA',
  success: '#22C55E',
  error: '#EF4444',
  warning: '#F59E0B',

  // Pastel category colors
  categories: {
    all: { bg: '#3B3B3B', icon: '#FFFFFF', label: 'All' },
    breakfast: { bg: '#FFB4C2', icon: '#1A1A1A', label: 'Breakfast' },
    soups: { bg: '#98D8AA', icon: '#1A1A1A', label: 'Soups' },
    pasta: { bg: '#C9B1FF', icon: '#1A1A1A', label: 'Pasta' },
    sushi: { bg: '#7DD3FC', icon: '#1A1A1A', label: 'Sushi' },
    dessert: { bg: '#FCD34D', icon: '#1A1A1A', label: 'Dessert' },
    drinks: { bg: '#FB923C', icon: '#1A1A1A', label: 'Drinks' },
    sides: { bg: '#F472B6', icon: '#1A1A1A', label: 'Sides' },
    grilled: { bg: '#86EFAC', icon: '#1A1A1A', label: 'Grilled' },
  } as Record<string, { bg: string; icon: string; label: string }>,

  // Light mode overrides
  light: {
    bg: '#F5F5F5',
    surface: '#FFFFFF',
    surfaceLight: '#FAFAFA',
    surfaceElevated: '#FFFFFF',
    border: '#E5E5E5',
    textPrimary: '#1A1A1A',
    textSecondary: '#525252',
    textMuted: '#A3A3A3',
  },
};

// Category icons mapping
const categoryIcons: Record<string, React.ReactNode> = {
  all: <CategoryIcon />,
  breakfast: <LocalCafeIcon />,
  soups: <RamenDiningIcon />,
  pasta: <RestaurantIcon />,
  sushi: <SetMealIcon />,
  dessert: <IcecreamIcon />,
  drinks: <LocalCafeIcon />,
  sides: <LocalPizzaIcon />,
  grilled: <KebabDiningIcon />,
  default: <LunchDiningIcon />,
};

export const POSPage = () => {
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const { mode } = useContext(ColorModeContext);
  const { animationsEnabled } = useAnimations();
  const { tier } = useTierAccess();
  const { isOffline, queueTransaction } = useContext(OfflineContext);
  const user = getUser();
  const searchInputRef = useRef<HTMLInputElement>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [rateLimitModalOpen, setRateLimitModalOpen] = useState(false);
  const [rateLimitInfo, setRateLimitInfo] = useState<{ limit: number; resetAt: string } | null>(null);
  const [activeStoreId, setActiveStoreId] = useState<string | null>(
    user?.defaultLocationId || user?.locationId || null
  );
  const [locationPromptOpen, setLocationPromptOpen] = useState(false);

  // Cash drawer shift state (open/close shift actions in the POS header)
  const [openShiftModalOpen, setOpenShiftModalOpen] = useState(false);
  const [closeShiftModalOpen, setCloseShiftModalOpen] = useState(false);

  // Barcode scanning states
  const [barcodeInput, setBarcodeInput] = useState('');
  const [scannerModalOpen, setScannerModalOpen] = useState(false);
  const barcodeInputRef = useRef<HTMLInputElement>(null);

  // Cart suspension states
  const [suspendModalOpen, setSuspendModalOpen] = useState(false);
  const [suspendedPanelOpen, setSuspendedPanelOpen] = useState(false);
  const [suspendedCarts, setSuspendedCarts] = useState<Cart[]>([]);
  const [loadingSuspended, setLoadingSuspended] = useState(false);
  const MAX_SUSPENDED_CARTS = 20;

  // Customer selection states
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [customerModalOpen, setCustomerModalOpen] = useState(false);

  // Discount state
  const [discount, setDiscount] = useState(0);

  // Return/Refund state
  const [returnModalOpen, setReturnModalOpen] = useState(false);

  // B2B mode state
  const [saleMode, setSaleMode] = useState<SaleMode>('b2c');
  const [selectedReseller, setSelectedReseller] = useState<ResellerProfile | null>(null);
  const [b2bPricing, setB2bPricing] = useState<B2BPricingResponse | null>(null);

  // Receipt printing state
  const [printReceipt, setPrintReceipt] = useState(false);

  // Payment status state (default: true = paid)
  const [isPaid, setIsPaid] = useState(true);

  // Fulfillment status state (default: true = completed)
  const [isCompleted, setIsCompleted] = useState(true);

  // Stock check dialog state
  const [stockCheckProduct, setStockCheckProduct] = useState<Product | null>(null);

  // Determine if we're in active B2B mode (mode is B2B and reseller is selected)
  const b2bMode = saleMode === 'b2b' && selectedReseller !== null;

  const isDark = mode === 'dark';
  const colors = isDark ? posColors : { ...posColors, ...posColors.light };

  // Fetch stores (locations of type 'store')
  const { data: storesData } = useQuery({
    queryKey: ['pos-stores'],
    queryFn: async () => {
      const response = await axiosInstance.get('/locations');
      const locations = Array.isArray(response.data) ? response.data : response.data.data || [];
      // Filter only stores (not warehouses)
      return locations.filter((loc: Location) => loc.type === 'store' && loc.isActive);
    },
  });

  const stores = useMemo(() => storesData || [], [storesData]);

  // Show location prompt if no location is set and stores are available
  useEffect(() => {
    if (!activeStoreId && stores.length > 0) {
      setLocationPromptOpen(true);
    }
  }, [activeStoreId, stores.length]);

  // State for "Save as default" checkbox in location prompt
  const [saveAsDefault, setSaveAsDefault] = useState(true);

  // Handle location selection from prompt modal
  const handleLocationPromptSelect = async (locationId: string) => {
    setActiveStoreId(locationId);
    setLocationPromptOpen(false);

    // Save as default location if checkbox is checked
    if (saveAsDefault) {
      try {
        await axiosInstance.patch('/users/me', { defaultLocationId: locationId });
        // Update local user in localStorage
        if (user) {
          setUser({ ...user, defaultLocationId: locationId });
        }
        showMessage('Default location saved', 'success');
      } catch (error) {
        showMessage('Failed to save default location', 'error');
      }
    }
  };

  // Get current store name for display
  const currentStore = useMemo(() => {
    if (!activeStoreId) return null;
    return stores.find((s: Location) => s.id === activeStoreId);
  }, [stores, activeStoreId]);

  // Current cash drawer shift for the active location (null = no open shift)
  const { data: currentShift } = useQuery({
    queryKey: ['cash-drawer-current-shift', activeStoreId],
    queryFn: async (): Promise<CashDrawerShift | null> => {
      const response = await axiosInstance.get('/cash-drawer/shifts/current', {
        params: { locationId: activeStoreId },
      });
      return response.data;
    },
    enabled: !!activeStoreId,
  });

  const isShiftOpen = currentShift?.status === CashDrawerShiftStatus.OPEN;

  // Fetch products - use search endpoint when searching, otherwise get all
  // Only fetch sellable products (not internal products like cleaning supplies)
  // Falls back to IndexedDB cache when offline
  const { data: productsData } = useQuery({
    queryKey: ['products', searchTerm, 'sellable'],
    queryFn: async () => {
      try {
        if (searchTerm && searchTerm.trim().length > 0) {
          // Use the search endpoint for searching
          const response = await axiosInstance.get('/products/search', {
            params: { q: searchTerm.trim() },
          });
          // Filter to only sellable products (backend search may not filter by type)
          const data = Array.isArray(response.data) ? response.data : response.data.data || [];
          return data.filter((p: any) => !p.productType || p.productType === 'sellable');
        } else {
          // Get all sellable products - use authenticated admin endpoint to ensure org filtering
          const response = await axiosInstance.get('/products/admin', {
            params: { limit: 100 },
          });
          // Filter to only sellable products on client side
          const data = Array.isArray(response.data) ? response.data : response.data.data || [];
          return data.filter((p: any) => !p.productType || p.productType === 'sellable');
        }
      } catch (error) {
        // If API fails (offline), try to load from IndexedDB cache
        const cachedProducts = await getProducts();
        // Filter to only sellable products
        const sellableProducts = cachedProducts.filter((p: Product) => !p.productType || p.productType === 'sellable');

        // If searching, filter cached products by search term
        if (searchTerm && searchTerm.trim().length > 0) {
          const term = searchTerm.trim().toLowerCase();
          return sellableProducts.filter((p: Product) =>
            p.name.toLowerCase().includes(term) ||
            (p.sku && p.sku.toLowerCase().includes(term))
          );
        }

        return sellableProducts;
      }
    },
  });

  const products = useMemo(() => productsData || [], [productsData]);

  // Fetch inventory for the selected store to check stock availability
  // Falls back to IndexedDB cache when offline
  const { data: inventoryData } = useQuery({
    queryKey: ['pos-inventory', activeStoreId],
    queryFn: async () => {
      if (!activeStoreId) return [];
      try {
        const response = await axiosInstance.get(`/inventory/location/${activeStoreId}`);
        const data = Array.isArray(response.data) ? response.data : response.data.data || [];
        // Cache immediately on successful fetch for offline use
        if (data.length > 0) {
          saveInventory(data, activeStoreId).catch((err) => {
            console.warn('Failed to cache inventory:', err);
          });
        }
        return data;
      } catch (error) {
        // If API fails (offline), try to load from IndexedDB cache
        console.warn('Inventory API failed, loading from cache');
        const cachedInventory = await getInventory(activeStoreId);
        return cachedInventory;
      }
    },
    enabled: !!activeStoreId,
  });

  // Create a map of product ID -> available stock
  const stockByProductId = useMemo(() => {
    const map = new Map<string, number>();
    if (inventoryData) {
      inventoryData.forEach((inv: any) => {
        const available = Math.max(0, (inv.quantity || 0) - (inv.reserved || 0) - (inv.allocated || 0));
        map.set(inv.productId, available);
      });
    }
    return map;
  }, [inventoryData]);

  // Cache products in IndexedDB when fetched
  useEffect(() => {
    if (products && products.length > 0) {
      saveProducts(products).catch((error) => {
        console.error('Failed to cache products:', error);
      });
    }
  }, [products]);

  // Cache inventory in IndexedDB when fetched
  useEffect(() => {
    if (inventoryData && inventoryData.length > 0 && activeStoreId) {
      saveInventory(inventoryData, activeStoreId).catch((error) => {
        console.error('Failed to cache inventory:', error);
      });
    }
  }, [inventoryData, activeStoreId]);

  // Auto-focus barcode input when POS opens
  useEffect(() => {
    const timer = setTimeout(() => {
      barcodeInputRef.current?.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Extract unique categories from products
  const categories = useMemo(() => {
    const categorySet = new Set<string>();
    products.forEach((product: Product) => {
      if (product.category) {
        categorySet.add(product.category);
      }
    });
    return Array.from(categorySet).sort();
  }, [products]);

  // Filter products by selected category and inventory presence at location
  const filteredProducts = useMemo(() => {
    let filtered = products;

    // Filter by inventory at location: only show products that have inventory records
    if (activeStoreId && inventoryData && inventoryData.length > 0) {
      const productIdsWithInventory = new Set(inventoryData.map((inv: any) => inv.productId));
      filtered = filtered.filter((p: Product) => productIdsWithInventory.has(p.id));
    }

    // Filter by category
    if (selectedCategory) {
      filtered = filtered.filter((p: Product) => p.category === selectedCategory);
    }

    return filtered;
  }, [products, selectedCategory, activeStoreId, inventoryData]);

  // Create category color mapping based on unique categories
  const categoryColors = useMemo(() => {
    const colorKeys = Object.keys(posColors.categories).filter(k => k !== 'all');
    const mapping: Record<string, { bg: string; icon: string; label: string }> = {
      all: posColors.categories.all,
    };

    categories.forEach((categoryName: string, index: number) => {
      const colorKey = colorKeys[index % colorKeys.length];
      mapping[categoryName] = {
        ...posColors.categories[colorKey],
        label: categoryName,
      };
    });

    return mapping;
  }, [categories]);

  // Prepare cart items for B2B pricing calculator
  const b2bCartItems = useMemo(() => {
    return cart.map((item) => ({
      productId: item.product.id,
      productName: item.product.name,
      quantity: item.quantity,
      retailPrice: item.price,
    }));
  }, [cart]);

  // Check if checkout is blocked (for button disabled state)
  const isCheckoutBlocked = useMemo(() => {
    // Empty cart
    if (cart.length === 0) return true;
    // Credit sale (unpaid) without customer in B2C mode
    if (!isPaid && saleMode === 'b2c' && !selectedCustomer) return true;
    // Credit sale (unpaid) without reseller in B2B mode
    if (!isPaid && saleMode === 'b2b' && !selectedReseller) return true;
    return false;
  }, [cart.length, isPaid, saleMode, selectedCustomer, selectedReseller]);

  const createOrderMutation = useMutation({
    mutationFn: (data: any) => {
      // Use unified /orders endpoint for all order types (B2C, B2B_DIRECT, B2B_QUOTE)
      return axiosInstance.post('/orders', data);
    },
    onSuccess: (response: any) => {
      showMessage('Order completed successfully', 'success');

      // Print receipt if requested and sale data is available
      if (printReceipt && response?.data) {
        handlePrintReceipt(response.data);
      }

      setCart([]);
      setSearchTerm('');
      setPaymentMethod('cash');
      setSelectedCustomer(null);
      setSelectedReseller(null);
      setB2bPricing(null);
      setDiscount(0);
      setSaleMode('b2c');
      setIsPaid(true);
      setIsCompleted(true);
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['sales'] }); // Keep for backward compatibility
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      searchInputRef.current?.focus();
    },
    onError: (error: any) => {
      if (error.response?.status === 429 && error.response?.data?.upgradeRequired) {
        setRateLimitInfo({
          limit: error.response.data.limit,
          resetAt: error.response.data.resetAt,
        });
        setRateLimitModalOpen(true);
      } else {
        showMessage(error.response?.data?.message || 'Failed to complete order', 'error');
      }
    },
  });

  const addToCart = (product: Product) => {
    const existingItem = cart.find((item) => item.product.id === product.id);
    if (existingItem) {
      setCart(
        cart.map((item) =>
          item.product.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        )
      );
    } else {
      setCart([...cart, { product, quantity: 1, price: product.price }]);
    }
  };

  const updateQuantity = (productId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(productId);
    } else {
      setCart(
        cart.map((item) =>
          item.product.id === productId ? { ...item, quantity } : item
        )
      );
    }
  };

  const removeFromCart = (productId: string) => {
    setCart(cart.filter((item) => item.product.id !== productId));
  };

  const calculateSubtotal = () => {
    return cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  };

  // Calculate B2B total from pricing response or fall back to regular total
  const calculateTotal = () => {
    // Use B2B pricing if available in B2B mode
    if (b2bMode && b2bPricing) {
      return b2bPricing.summary.grandTotal;
    }
    // Standard B2C calculation with discount
    const subtotal = calculateSubtotal();
    return Math.max(0, subtotal - discount);
  };

  const handleCheckout = async () => {
    if (cart.length === 0) {
      showMessage('Cart is empty', 'warning');
      return;
    }

    // Validate B2B mode requirements
    if (saleMode === 'b2b' && !selectedReseller) {
      showMessage('Please select a reseller for B2B sale', 'warning');
      return;
    }

    // Validate credit sale requirements: customer is required for unpaid orders
    if (!isPaid && saleMode === 'b2c' && !selectedCustomer) {
      showMessage('Un client est requis pour les ventes à crédit', 'error');
      return;
    }
    if (!isPaid && saleMode === 'b2b' && !selectedReseller) {
      showMessage('Un revendeur est requis pour les ventes B2B à crédit', 'error');
      return;
    }

    const storeId = activeStoreId || user?.defaultLocationId || user?.locationId;
    if (!storeId) {
      showMessage('Please select a location before processing orders.', 'error');
      setLocationPromptOpen(true);
      return;
    }

    // Check if offline - queue transaction instead of sending to server
    if (isOffline) {
      try {
        const total = calculateTotal();
        const localId = `offline-sale-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

        // Create queued order items matching QueuedOrderItem structure
        const queuedItems = cart.map((item) => ({
          productId: item.product.id,
          productName: item.product.name,
          quantity: item.quantity,
          unitPrice: item.price,
          subtotal: item.price * item.quantity,
        }));

        const subtotalAmount = calculateSubtotal();
        const queuedOrder = {
          localId,
          queuedAt: new Date().toISOString(),
          orderType: saleMode === 'b2b' ? OrderType.B2B_DIRECT : OrderType.B2C,
          paymentStatus: isPaid ? OrderPaymentStatus.PAID : OrderPaymentStatus.UNPAID,
          fulfillmentStatus: isCompleted ? OrderFulfillmentStatus.COMPLETED : OrderFulfillmentStatus.PENDING,
          fulfillmentMethod: FulfillmentMethod.PICKUP,
          locationId: storeId,
          customerId: selectedCustomer?.id,
          staffId: user?.id,
          subtotal: subtotalAmount,
          tax: 0,
          discount: discount,
          totalAmount: total,
          totalItems: queuedItems.length,
          paymentMethod: paymentMethod as 'cash' | 'card' | 'mobile' | 'other',
          items: queuedItems,
          notes: 'Offline sale - pending sync',
        };

        await queueTransaction(queuedOrder);

        showMessage('Sale queued for sync when online', 'success');

        // Print receipt for offline sale if requested
        if (printReceipt) {
          try {
            handlePrintReceipt({
              id: localId,
              createdAt: queuedOrder.queuedAt,
              subtotalAmount: subtotalAmount,
              discountAmount: discount,
              taxAmount: 0,
              totalAmount: total,
              paymentMethod: paymentMethod,
              items: cart.map((item) => ({
                id: `temp-${item.product.id}`,
                saleId: localId,
                productId: item.product.id,
                product: item.product,
                quantity: item.quantity,
                price: item.price,
              })),
            });
          } catch (error) {
            // Don't block the sale if printing fails
          }
        }

        setCart([]);
        setSearchTerm('');
        setPaymentMethod('cash');
        setSelectedCustomer(null);
        setSelectedReseller(null);
        setB2bPricing(null);
        setDiscount(0);
        setSaleMode('b2c');
        setIsPaid(true);
        setIsCompleted(true);
        searchInputRef.current?.focus();
      } catch (error: any) {
        showMessage(error?.message || 'Failed to queue sale', 'error');
      }
      return;
    }

    // Online - proceed with normal API call using unified /orders endpoint
    // Use B2B_DIRECT order type if in B2B mode with a selected reseller
    if (saleMode === 'b2b' && selectedReseller) {
      createOrderMutation.mutate({
        orderType: OrderType.B2B_DIRECT,
        locationId: storeId,
        fulfillmentMethod: 'pickup',
        paymentMethod,
        paymentStatus: isPaid ? OrderPaymentStatus.PAID : OrderPaymentStatus.UNPAID,
        fulfillmentStatus: isCompleted ? OrderFulfillmentStatus.COMPLETED : OrderFulfillmentStatus.PENDING,
        resellerId: selectedReseller.id,
        pricingTierId: selectedReseller.defaultTierId || undefined,
        staffId: user?.id,
        items: cart.map((item) => ({
          productId: item.product.id,
          quantity: item.quantity,
        })),
      });
    } else {
      // B2C order type for regular POS sales
      createOrderMutation.mutate({
        orderType: OrderType.B2C,
        locationId: storeId,
        fulfillmentMethod: 'pickup',
        paymentMethod,
        paymentStatus: isPaid ? OrderPaymentStatus.PAID : OrderPaymentStatus.UNPAID,
        fulfillmentStatus: isCompleted ? OrderFulfillmentStatus.COMPLETED : OrderFulfillmentStatus.PENDING,
        customerId: selectedCustomer?.id,
        discount: discount > 0 ? discount : undefined,
        staffId: user?.id,
        items: cart.map((item) => ({
          productId: item.product.id,
          quantity: item.quantity,
        })),
      });
    }
  };

  /**
   * Print receipt for a sale
   * Opens a new window with the receipt and triggers browser print dialog
   */
  const handlePrintReceipt = (saleData: any) => {
    try {
      // Create a new window for printing
      const printWindow = window.open('', '_blank', 'width=300,height=600');
      if (!printWindow) {
        showMessage('Please allow popups to print receipts', 'warning');
        return;
      }

      // Set up the print window
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Receipt</title>
            <style>
              body { margin: 0; padding: 0; font-family: monospace; }
              @media print {
                body { margin: 0; padding: 0; }
              }
            </style>
          </head>
          <body>
            <div id="receipt-root"></div>
          </body>
        </html>
      `);
      printWindow.document.close();

      // Wait for window to load, then render Receipt component
      printWindow.onload = () => {
        const rootElement = printWindow.document.getElementById('receipt-root');
        if (rootElement) {
          const root = createRoot(rootElement);
          root.render(
            <Receipt
              sale={saleData}
              organization={undefined}
              location={undefined}
            />
          );

          // Trigger print dialog after a short delay
          setTimeout(() => {
            printWindow.print();
            // Close window after printing or if user cancels
            setTimeout(() => {
              printWindow.close();
            }, 100);
          }, 500);
        }
      };
    } catch (error) {
      console.error('Failed to print receipt:', error);
      showMessage('Failed to print receipt', 'error');
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && cart.length > 0) {
        e.preventDefault();
        handleCheckout();
      }
      if (e.key === 'Escape' && searchTerm) {
        e.preventDefault();
        setSearchTerm('');
        searchInputRef.current?.focus();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, searchTerm]);

  const paymentMethods = [
    { value: 'cash', label: 'Cash', icon: LocalAtmIcon },
    { value: 'card', label: 'Card', icon: CreditCardIcon },
    { value: 'mobile', label: 'Mobile', icon: PhoneAndroidIcon },
  ];

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('fr-DZ', {
      style: 'currency',
      currency: 'DZD',
      minimumFractionDigits: 0,
    }).format(price);
  };

  // Barcode search function
  const handleBarcodeSearch = async (barcode: string) => {
    if (!barcode.trim()) return;

    try {
      const response = await axiosInstance.get<BarcodeSearchResponse>(
        `/products/barcode/${encodeURIComponent(barcode.trim())}`,
        { params: { locationId: activeStoreId } }
      );

      if (response.data.found && response.data.product) {
        addToCart(response.data.product);
        setBarcodeInput('');
        showMessage(`${response.data.product.name} ajouté au panier`, 'success');
      } else {
        showMessage('Produit non trouvé', 'warning');
      }
    } catch (error: any) {
      showMessage(error.response?.data?.message || 'Erreur de recherche', 'error');
    }

    barcodeInputRef.current?.focus();
  };

  // Load suspended carts
  const loadSuspendedCarts = useCallback(async () => {
    if (!activeStoreId) return;
    setLoadingSuspended(true);
    try {
      const response = await axiosInstance.get<Cart[]>('/cart/suspended', {
        params: { locationId: activeStoreId },
      });
      setSuspendedCarts(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to load suspended carts:', error);
    } finally {
      setLoadingSuspended(false);
    }
  }, [activeStoreId]);

  // Suspend current cart
  const handleSuspendCart = async (customerName: string, notes: string) => {
    if (cart.length === 0) {
      showMessage('Le panier est vide', 'warning');
      return;
    }

    if (suspendedCarts.length >= MAX_SUSPENDED_CARTS) {
      showMessage(`Maximum ${MAX_SUSPENDED_CARTS} paniers en attente atteint`, 'error');
      return;
    }

    try {
      // Create a cart on the server and suspend it
      const createResponse = await axiosInstance.post('/cart', {
        locationId: activeStoreId,
        items: cart.map((item) => ({
          productId: item.product.id,
          quantity: item.quantity,
        })),
      });

      // Now suspend the cart
      await axiosInstance.post(`/cart/${createResponse.data.id}/suspend`, {
        customerName: customerName || undefined,
        notes: notes || undefined,
      });

      setCart([]);
      showMessage('Panier mis en attente', 'success');
      loadSuspendedCarts();
    } catch (error: any) {
      showMessage(error.response?.data?.message || 'Erreur de suspension', 'error');
      throw error;
    }
  };

  // Resume a suspended cart
  const handleResumeCart = async (cartId: string) => {
    try {
      const response = await axiosInstance.post<Cart>(`/cart/${cartId}/resume`);
      const resumedCart = response.data;

      // Replace current cart with resumed cart items
      if (resumedCart.items && resumedCart.items.length > 0) {
        setCart(
          resumedCart.items.map((item) => ({
            product: item.product!,
            quantity: item.quantity,
            price: item.price,
          }))
        );
      }

      setSuspendedPanelOpen(false);
      showMessage('Panier repris', 'success');
      loadSuspendedCarts();
    } catch (error: any) {
      showMessage(error.response?.data?.message || 'Erreur de reprise', 'error');
      throw error;
    }
  };

  // Delete a suspended cart
  const handleDeleteSuspendedCart = async (cartId: string) => {
    try {
      await axiosInstance.delete(`/cart/${cartId}/suspended`);
      showMessage('Panier supprimé', 'success');
      loadSuspendedCarts();
    } catch (error: any) {
      showMessage(error.response?.data?.message || 'Erreur de suppression', 'error');
      throw error;
    }
  };

  // Load suspended carts when store changes
  useEffect(() => {
    if (activeStoreId) {
      loadSuspendedCarts();
    }
  }, [activeStoreId, loadSuspendedCarts]);

  return (
    <Box
      sx={{
        height: 'calc(100vh - 64px)',
        maxHeight: 'calc(100vh - 64px)',
        background: colors.bg,
        display: 'flex',
        overflow: 'hidden',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        ...(animationsEnabled ? {
          animation: 'fadeIn 0.4s ease-out',
          '@keyframes fadeIn': {
            from: { opacity: 0 },
            to: { opacity: 1 },
          },
        } : {}),
      }}
    >
      {/* LEFT PANEL - Categories & Products */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          p: 2.5,
          overflow: 'hidden',
        }}
      >
        {/* Header with Search */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '12px',
                background: `linear-gradient(135deg, ${posColors.accent} 0%, ${posColors.accentLight} 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: `0 4px 20px ${alpha(posColors.accent, 0.4)}`,
              }}
            >
              <ReceiptLongIcon sx={{ color: '#FFF', fontSize: 24 }} />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontSize: '20px',
                  fontWeight: 700,
                  color: colors.textPrimary,
                  letterSpacing: '-0.3px',
                }}
              >
                Point of Sale
              </Typography>
              {currentStore && (
                <Typography
                  sx={{
                    fontSize: '12px',
                    color: colors.textMuted,
                    fontWeight: 500,
                  }}
                >
                  {currentStore.name}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Barcode Scanner Input */}
          <Box sx={{ flex: 1, maxWidth: 280 }}>
            <TextField
              fullWidth
              size="small"
              inputRef={barcodeInputRef}
              value={barcodeInput}
              onChange={(e) => setBarcodeInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleBarcodeSearch(barcodeInput);
                }
              }}
              placeholder="Scanner code-barres..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <QrCodeScannerIcon sx={{ color: posColors.accent, fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setScannerModalOpen(true)}
                      sx={{ color: colors.textMuted }}
                    >
                      <CameraAltIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: colors.surface,
                  borderRadius: '10px',
                  fontSize: '14px',
                  '& fieldset': {
                    borderColor: posColors.accent,
                    borderWidth: 1.5,
                  },
                  '&:hover fieldset': { borderColor: posColors.accentLight },
                  '&.Mui-focused fieldset': {
                    borderColor: posColors.accent,
                    borderWidth: 2,
                  },
                },
                '& input': {
                  color: colors.textPrimary,
                  '&::placeholder': { color: colors.textMuted },
                },
              }}
            />
          </Box>

          {/* Search Bar */}
          <Box sx={{ flex: 1, maxWidth: 300 }}>
            <TextField
              fullWidth
              size="small"
              inputRef={searchInputRef}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Rechercher produit..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: colors.textMuted, fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: colors.surface,
                  borderRadius: '10px',
                  fontSize: '14px',
                  '& fieldset': {
                    borderColor: colors.border,
                    borderWidth: 1,
                  },
                  '&:hover fieldset': { borderColor: colors.textMuted },
                  '&.Mui-focused fieldset': {
                    borderColor: posColors.accent,
                    borderWidth: 2,
                  },
                },
                '& input': {
                  color: colors.textPrimary,
                  '&::placeholder': { color: colors.textMuted },
                },
              }}
            />
          </Box>

          {/* Process Return Button */}
          <IconButton
            onClick={() => setReturnModalOpen(true)}
            sx={{
              bgcolor: colors.surface,
              border: `1px solid ${colors.border}`,
              '&:hover': { bgcolor: colors.surfaceLight },
            }}
            title="Process Return"
          >
            <UndoIcon sx={{ color: posColors.warning }} />
          </IconButton>

          {/* Suspended Carts Button */}
          <IconButton
            onClick={() => setSuspendedPanelOpen(true)}
            sx={{
              bgcolor: colors.surface,
              border: `1px solid ${colors.border}`,
              '&:hover': { bgcolor: colors.surfaceLight },
            }}
          >
            <Badge badgeContent={suspendedCarts.length} color="warning">
              <PauseCircleOutlineIcon sx={{ color: colors.textSecondary }} />
            </Badge>
          </IconButton>

          {/* Cash Drawer Shift Indicator + Open/Close Actions */}
          <Chip
            icon={isShiftOpen ? <LockOpenIcon /> : <LockIcon />}
            label={
              isShiftOpen
                ? `Shift Open${currentShift ? ` (${formatPrice(currentShift.openingBalance)})` : ''}`
                : 'Shift Closed'
            }
            size="small"
            color={isShiftOpen ? 'success' : 'default'}
            variant={isShiftOpen ? 'filled' : 'outlined'}
          />
          <Button
            size="small"
            variant="outlined"
            onClick={() => (isShiftOpen ? setCloseShiftModalOpen(true) : setOpenShiftModalOpen(true))}
            disabled={!activeStoreId}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {isShiftOpen ? 'Close Shift' : 'Open Shift'}
          </Button>

          {/* Offline Indicator */}
          <OfflineIndicator />
        </Box>

        {/* Category Grid */}
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: 600,
              color: colors.textSecondary,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              mb: 1.5,
            }}
          >
            Categories
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
              gap: 1.5,
            }}
          >
            {/* All category */}
            <CategoryCard
              id={null}
              colors={categoryColors.all}
              icon={categoryIcons.all}
              isSelected={selectedCategory === null}
              onClick={() => setSelectedCategory(null)}
              animationsEnabled={animationsEnabled}
              index={0}
            />

            {/* Dynamic categories from products */}
            {categories.map((categoryName: string, index: number) => (
              <CategoryCard
                key={categoryName}
                id={categoryName}
                colors={categoryColors[categoryName] || posColors.categories.breakfast}
                icon={categoryIcons[categoryName?.toLowerCase()] || categoryIcons.default}
                isSelected={selectedCategory === categoryName}
                onClick={() => setSelectedCategory(categoryName)}
                animationsEnabled={animationsEnabled}
                index={index + 1}
              />
            ))}
          </Box>
        </Box>

        {/* Product Grid */}
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: 600,
              color: colors.textSecondary,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              mb: 1.5,
            }}
          >
            Menu Items ({filteredProducts.length})
          </Typography>
          <Box
            sx={{
              height: 'calc(100% - 32px)',
              overflowY: 'auto',
              pr: 1,
              '&::-webkit-scrollbar': { width: 6 },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: colors.border,
                borderRadius: 3,
                '&:hover': { backgroundColor: colors.textMuted },
              },
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                gap: 2,
                pb: 2,
              }}
            >
              {filteredProducts.map((product: Product, index: number) => {
                const cartItem = cart.find((item) => item.product.id === product.id);
                const quantity = cartItem?.quantity || 0;
                const availableStock = stockByProductId.get(product.id) ?? 0;
                const isOutOfStock = activeStoreId ? availableStock <= 0 : false;

                return (
                  <ProductCard
                    key={product.id}
                    product={product}
                    quantity={quantity}
                    isOutOfStock={isOutOfStock}
                    availableStock={availableStock}
                    onAdd={() => addToCart(product)}
                    onIncrease={() => updateQuantity(product.id, quantity + 1)}
                    onDecrease={() => updateQuantity(product.id, quantity - 1)}
                    onStockCheck={() => setStockCheckProduct(product)}
                    formatPrice={formatPrice}
                    colors={colors}
                    isDark={isDark}
                    animationsEnabled={animationsEnabled}
                    index={index}
                  />
                );
              })}

              {filteredProducts.length === 0 && (
                <Box
                  sx={{
                    gridColumn: '1 / -1',
                    py: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <SearchIcon sx={{ fontSize: 48, color: colors.textMuted, opacity: 0.5 }} />
                  <Typography sx={{ color: colors.textMuted, fontSize: '14px' }}>
                    No products found
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* RIGHT PANEL - Order Summary */}
      <Box
        sx={{
          width: 360,
          backgroundColor: colors.surface,
          borderLeft: `1px solid ${colors.border}`,
          display: 'flex',
          flexDirection: 'column',
          ...(animationsEnabled ? {
            animation: 'slideInRight 0.4s ease-out',
            '@keyframes slideInRight': {
              from: { opacity: 0, transform: 'translateX(20px)' },
              to: { opacity: 1, transform: 'translateX(0)' },
            },
          } : {}),
        }}
      >
        {/* Order Header with Location Selector */}
        <Box
          sx={{
            p: 2.5,
            borderBottom: `1px solid ${colors.border}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: 700,
                color: colors.textPrimary,
              }}
            >
              Current Order
            </Typography>
            {/* B2B Mode Toggle */}
            <B2BModeToggle
              mode={saleMode}
              onModeChange={(newMode) => {
                setSaleMode(newMode);
                // Clear reseller and B2B pricing when switching to B2C
                if (newMode === 'b2c') {
                  setSelectedReseller(null);
                  setB2bPricing(null);
                }
                // Clear regular customer when switching to B2B
                if (newMode === 'b2b') {
                  setSelectedCustomer(null);
                }
              }}
              colors={colors}
            />
          </Box>
          {/* Location Selector - Always visible in header */}
          <LocationSelector
            stores={stores}
            activeStoreId={activeStoreId}
            onLocationChange={setActiveStoreId}
            colors={colors}
          />
          {/* Customer/Reseller Selector - Based on sale mode */}
          <Box sx={{ mt: 1.5 }}>
            {saleMode === 'b2b' ? (
              <ResellerSelector
                selectedReseller={selectedReseller}
                onResellerSelect={setSelectedReseller}
                colors={colors}
              />
            ) : (
              <CustomerSelector
                selectedCustomer={selectedCustomer}
                onCustomerSelect={setSelectedCustomer}
                onCreateClick={() => setCustomerModalOpen(true)}
                colors={colors}
                saleMode={saleMode}
              />
            )}
          </Box>
        </Box>

        {/* Order Items */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 2,
            '&::-webkit-scrollbar': { width: 6 },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: colors.border,
              borderRadius: 3,
            },
          }}
        >
          {cart.length === 0 ? (
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.5,
                color: colors.textMuted,
              }}
            >
              <ReceiptLongIcon sx={{ fontSize: 48, opacity: 0.4 }} />
              <Typography sx={{ fontSize: '14px' }}>No items in order</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {cart.map((item, index) => (
                <OrderItem
                  key={item.product.id}
                  item={item}
                  index={index}
                  onRemove={() => removeFromCart(item.product.id)}
                  onIncrease={() => updateQuantity(item.product.id, item.quantity + 1)}
                  onDecrease={() => updateQuantity(item.product.id, item.quantity - 1)}
                  formatPrice={formatPrice}
                  colors={colors}
                  animationsEnabled={animationsEnabled}
                />
              ))}
            </Box>
          )}
        </Box>

        {/* Payment Section */}
        {cart.length > 0 && (
          <Box
            sx={{
              p: 2.5,
              borderTop: `1px solid ${colors.border}`,
              backgroundColor: colors.surfaceLight,
            }}
          >
            {/* Payment Methods */}
            <Box sx={{ mb: 2.5 }}>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 600,
                  color: colors.textMuted,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  mb: 1,
                }}
              >
                Payment Method
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {paymentMethods.map((method) => {
                  const Icon = method.icon;
                  const isSelected = paymentMethod === method.value;
                  return (
                    <Button
                      key={method.value}
                      onClick={() => setPaymentMethod(method.value)}
                      sx={{
                        flex: 1,
                        py: 1.5,
                        borderRadius: '10px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                        backgroundColor: isSelected
                          ? alpha(posColors.accent, 0.15)
                          : colors.surface,
                        border: `2px solid ${
                          isSelected ? posColors.accent : 'transparent'
                        }`,
                        color: isSelected ? posColors.accent : colors.textSecondary,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: isSelected
                            ? alpha(posColors.accent, 0.2)
                            : alpha(colors.textMuted, 0.1),
                        },
                      }}
                    >
                      <Icon sx={{ fontSize: 20 }} />
                      <Typography
                        sx={{
                          fontSize: '10px',
                          fontWeight: 600,
                          textTransform: 'none',
                        }}
                      >
                        {method.label}
                      </Typography>
                    </Button>
                  );
                })}
              </Box>
            </Box>

            {/* Receipt Printing Toggle */}
            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={printReceipt}
                    onChange={(e) => setPrintReceipt(e.target.checked)}
                    size="small"
                    sx={{
                      color: colors.textMuted,
                      '&.Mui-checked': {
                        color: posColors.accent,
                      },
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: '13px',
                      fontWeight: 500,
                      color: colors.textSecondary,
                    }}
                  >
                    Imprimer reçu
                  </Typography>
                }
                sx={{
                  ml: 0,
                  '& .MuiFormControlLabel-label': {
                    ml: 0.5,
                  },
                }}
              />
            </Box>

            {/* Payment Status Toggle */}
            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isPaid}
                    onChange={(e) => setIsPaid(e.target.checked)}
                    size="small"
                    sx={{
                      color: colors.textMuted,
                      '&.Mui-checked': {
                        color: posColors.accent,
                      },
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: '13px',
                      fontWeight: 500,
                      color: colors.textSecondary,
                    }}
                  >
                    Paiement effectué
                  </Typography>
                }
                sx={{
                  ml: 0,
                  '& .MuiFormControlLabel-label': {
                    ml: 0.5,
                  },
                }}
              />
            </Box>

            {/* Fulfillment Status Toggle */}
            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isCompleted}
                    onChange={(e) => setIsCompleted(e.target.checked)}
                    size="small"
                    sx={{
                      color: colors.textMuted,
                      '&.Mui-checked': {
                        color: posColors.accent,
                      },
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: '13px',
                      fontWeight: 500,
                      color: colors.textSecondary,
                    }}
                  >
                    Commande terminée
                  </Typography>
                }
                sx={{
                  ml: 0,
                  '& .MuiFormControlLabel-label': {
                    ml: 0.5,
                  },
                }}
              />
            </Box>

            {/* Order Summary - B2B or B2C */}
            {saleMode === 'b2b' ? (
              /* B2B Pricing Summary */
              <Box sx={{ mb: 2 }}>
                {/* B2B Mode Indicator */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 1.5,
                    py: 0.75,
                    px: 1,
                    backgroundColor: alpha(posColors.accent, 0.1),
                    borderRadius: '8px',
                    border: `1px solid ${alpha(posColors.accent, 0.3)}`,
                  }}
                >
                  <BusinessIcon sx={{ fontSize: 16, color: posColors.accent }} />
                  <Typography
                    sx={{
                      fontSize: '12px',
                      fontWeight: 600,
                      color: posColors.accent,
                    }}
                  >
                    B2B Sale - Tier pricing applied
                  </Typography>
                </Box>

                {/* B2B Pricing Calculator */}
                <B2BPricingCalculator
                  reseller={selectedReseller}
                  items={b2bCartItems}
                  colors={colors}
                  compact={true}
                  onPricingCalculated={setB2bPricing}
                />
              </Box>
            ) : (
              /* B2C Standard Summary */
              <Box
                sx={{
                  mb: 2,
                  py: 1.5,
                  px: 2,
                  backgroundColor: colors.surface,
                  borderRadius: '10px',
                }}
              >
                {/* Subtotal */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1.5,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '13px',
                      fontWeight: 500,
                      color: colors.textSecondary,
                    }}
                  >
                    Subtotal
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontWeight: 600,
                      color: colors.textPrimary,
                    }}
                  >
                    {formatPrice(calculateSubtotal())}
                  </Typography>
                </Box>

                {/* Discount Input */}
                <Box sx={{ mb: 1.5 }}>
                  <DiscountInput
                    subtotal={calculateSubtotal()}
                    discount={discount}
                    onDiscountChange={setDiscount}
                    formatPrice={formatPrice}
                    colors={colors}
                    disabled={cart.length === 0}
                  />
                </Box>

                {/* Discount Display (when applied) */}
                {discount > 0 && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 1.5,
                      py: 0.5,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '13px',
                        fontWeight: 500,
                        color: posColors.success,
                      }}
                    >
                      Discount Applied
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        fontWeight: 600,
                        color: posColors.success,
                      }}
                    >
                      -{formatPrice(discount)}
                    </Typography>
                  </Box>
                )}

                {/* Divider */}
                <Box
                  sx={{
                    height: '1px',
                    backgroundColor: colors.border,
                    my: 1.5,
                  }}
                />

                {/* Total */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontWeight: 600,
                      color: colors.textSecondary,
                    }}
                  >
                    Total
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '24px',
                      fontWeight: 800,
                      color: colors.textPrimary,
                      letterSpacing: '-0.5px',
                    }}
                  >
                    {formatPrice(calculateTotal())}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              {/* Suspend Cart Button */}
              <Button
                variant="outlined"
                onClick={() => setSuspendModalOpen(true)}
                disabled={cart.length === 0 || createOrderMutation.isPending}
                startIcon={<PauseCircleOutlineIcon />}
                sx={{
                  py: 1.5,
                  borderRadius: '12px',
                  fontSize: '13px',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderColor: posColors.warning,
                  color: posColors.warning,
                  '&:hover': {
                    borderColor: posColors.warning,
                    backgroundColor: alpha(posColors.warning, 0.1),
                  },
                  '&:disabled': {
                    borderColor: colors.border,
                    color: colors.textMuted,
                  },
                }}
              >
                Attente
              </Button>

              {/* Complete Order Button */}
              <Button
                fullWidth
                variant="contained"
                onClick={handleCheckout}
                disabled={createOrderMutation.isPending || isCheckoutBlocked}
                sx={{
                  py: 2,
                  borderRadius: '12px',
                  fontSize: '15px',
                  fontWeight: 700,
                  textTransform: 'none',
                  background: `linear-gradient(135deg, ${posColors.accent} 0%, ${posColors.accentLight} 100%)`,
                  boxShadow: `0 4px 20px ${alpha(posColors.accent, 0.4)}`,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 6px 28px ${alpha(posColors.accent, 0.5)}`,
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                  },
                  '&:disabled': {
                    background: colors.textMuted,
                    boxShadow: 'none',
                  },
                }}
              >
                {createOrderMutation.isPending ? 'En cours...' : 'Valider'}
              </Button>
            </Box>
          </Box>
        )}

      </Box>

      {/* Rate Limit Upgrade Modal */}
      <UpgradeModal
        open={rateLimitModalOpen}
        onClose={() => setRateLimitModalOpen(false)}
        feature="Unlimited Sales"
        currentTier={tier}
        customMessage={
          rateLimitInfo
            ? `You've reached your sales rate limit (${rateLimitInfo.limit} per hour). Your limit will reset at ${new Date(rateLimitInfo.resetAt).toLocaleTimeString()}. Upgrade to remove all sales limits.`
            : undefined
        }
      />

      {/* Barcode Scanner Modal */}
      <BarcodeScannerModal
        open={scannerModalOpen}
        onClose={() => setScannerModalOpen(false)}
        onScan={handleBarcodeSearch}
        continuousMode={true}
      />

      {/* Suspend Cart Modal */}
      <SuspendCartModal
        open={suspendModalOpen}
        onClose={() => setSuspendModalOpen(false)}
        onConfirm={handleSuspendCart}
        itemCount={cart.reduce((sum, item) => sum + item.quantity, 0)}
        total={calculateTotal()}
        formatPrice={formatPrice}
      />

      {/* Suspended Carts Panel */}
      <SuspendedCartsPanel
        open={suspendedPanelOpen}
        onClose={() => setSuspendedPanelOpen(false)}
        suspendedCarts={suspendedCarts}
        isLoading={loadingSuspended}
        onResume={handleResumeCart}
        onDelete={handleDeleteSuspendedCart}
        formatPrice={formatPrice}
        maxCarts={MAX_SUSPENDED_CARTS}
      />
      <QuickCustomerModal
        open={customerModalOpen}
        onClose={() => setCustomerModalOpen(false)}
        onCustomerCreated={(customer) => {
          setSelectedCustomer(customer);
          setCustomerModalOpen(false);
        }}
        colors={colors}
      />

      {/* Return Lookup Modal */}
      <ReturnLookupModal
        open={returnModalOpen}
        onClose={() => setReturnModalOpen(false)}
        onRefundSuccess={() => {
          showMessage('Refund processed successfully', 'success');
          setReturnModalOpen(false);
        }}
        colors={colors}
      />

      {/* Cash Drawer: Open Shift Modal */}
      <OpenShiftModal
        open={openShiftModalOpen}
        onClose={() => setOpenShiftModalOpen(false)}
        defaultLocationId={activeStoreId || undefined}
        locationName={currentStore?.name}
      />

      {/* Cash Drawer: Close Shift Modal */}
      <CloseShiftModal
        open={closeShiftModalOpen}
        onClose={() => setCloseShiftModalOpen(false)}
        shift={currentShift || null}
      />

      {/* Location Selection Prompt Modal */}
      <Dialog
        open={locationPromptOpen}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: '8px',
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: colors.surfaceLight,
            borderBottom: `1px solid ${colors.border}`,
            color: colors.textPrimary,
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <StoreIcon sx={{ color: posColors.accent }} />
          Select a Location
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Typography
            sx={{
              px: 3,
              pt: 2,
              pb: 1,
              color: colors.textSecondary,
              fontSize: '14px',
            }}
          >
            Please select a location to continue. You can set a default location in Settings to skip this step.
          </Typography>
          <List sx={{ pt: 0 }}>
            {stores.map((store: Location) => (
              <ListItemButton
                key={store.id}
                onClick={() => handleLocationPromptSelect(store.id)}
                sx={{
                  borderBottom: `1px solid ${colors.border}`,
                  '&:hover': {
                    backgroundColor: alpha(posColors.accent, 0.1),
                  },
                }}
              >
                <ListItemIcon>
                  <StoreIcon sx={{ color: posColors.accent }} />
                </ListItemIcon>
                <ListItemText
                  primary={store.name}
                  secondary={store.address}
                  primaryTypographyProps={{
                    color: colors.textPrimary,
                    fontWeight: 600,
                  }}
                  secondaryTypographyProps={{
                    color: colors.textSecondary,
                    fontSize: '12px',
                  }}
                />
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: colors.surfaceLight, borderTop: `1px solid ${colors.border}`, p: 2, flexDirection: 'column', alignItems: 'flex-start' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={saveAsDefault}
                onChange={(e) => setSaveAsDefault(e.target.checked)}
                sx={{
                  color: posColors.accent,
                  '&.Mui-checked': { color: posColors.accent },
                }}
              />
            }
            label={
              <Typography sx={{ color: colors.textPrimary, fontSize: '13px' }}>
                Save as my default location
              </Typography>
            }
          />
          <Typography
            variant="caption"
            sx={{ color: colors.textMuted, fontSize: '11px', mt: 1 }}
          >
            You can change this later in Settings
          </Typography>
        </DialogActions>
      </Dialog>

      {/* Product Stock Check Dialog */}
      <ProductInventoryDialog
        open={!!stockCheckProduct}
        onClose={() => setStockCheckProduct(null)}
        product={stockCheckProduct}
      />
    </Box>
  );
};

// Category Card Component
interface CategoryCardProps {
  id: string | null;
  colors: { bg: string; icon: string; label: string };
  icon: React.ReactNode;
  isSelected: boolean;
  onClick: () => void;
  animationsEnabled: boolean;
  index: number;
}

const CategoryCard: React.FC<CategoryCardProps> = ({
  colors,
  icon,
  isSelected,
  onClick,
  animationsEnabled,
  index,
}) => {
  return (
    <Box
      onClick={onClick}
      sx={{
        p: 1.5,
        borderRadius: '14px',
        backgroundColor: colors.bg,
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.75,
        transition: 'all 0.2s ease',
        border: `2px solid ${isSelected ? alpha('#FFF', 0.3) : 'transparent'}`,
        transform: isSelected ? 'scale(1.02)' : 'scale(1)',
        boxShadow: isSelected
          ? `0 8px 24px ${alpha(colors.bg, 0.5)}`
          : `0 2px 8px ${alpha('#000', 0.2)}`,
        ...(animationsEnabled ? {
          animation: `scaleIn 0.3s ease-out ${index * 0.05}s both`,
          '@keyframes scaleIn': {
            from: { opacity: 0, transform: 'scale(0.9)' },
            to: { opacity: 1, transform: 'scale(1)' },
          },
        } : {}),
        '&:hover': {
          transform: 'scale(1.05)',
          boxShadow: `0 8px 24px ${alpha(colors.bg, 0.6)}`,
        },
        '&:active': {
          transform: 'scale(0.98)',
        },
      }}
    >
      <Box
        sx={{
          fontSize: 24,
          color: colors.icon,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {icon}
      </Box>
      <Typography
        sx={{
          fontSize: '11px',
          fontWeight: 600,
          color: colors.icon,
          textAlign: 'center',
          lineHeight: 1.2,
        }}
      >
        {colors.label}
      </Typography>
    </Box>
  );
};

// Product Card Component
interface ProductCardProps {
  product: Product;
  quantity: number;
  isOutOfStock?: boolean;
  availableStock?: number;
  onAdd: () => void;
  onIncrease: () => void;
  onDecrease: () => void;
  onStockCheck: () => void;
  formatPrice: (price: number) => string;
  colors: any;
  isDark: boolean;
  animationsEnabled: boolean;
  index: number;
}

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  quantity,
  isOutOfStock = false,
  availableStock = 0,
  onAdd,
  onIncrease,
  onDecrease,
  onStockCheck,
  formatPrice,
  colors,
  isDark,
  animationsEnabled,
  index,
}) => {
  const isInCart = quantity > 0;

  return (
    <Box
      onClick={() => {
        if (!isOutOfStock && !isInCart) {
          onAdd();
        }
      }}
      sx={{
        backgroundColor: colors.surface,
        borderRadius: '16px',
        overflow: 'hidden',
        border: `1px solid ${isOutOfStock ? posColors.error : isInCart ? posColors.accent : colors.border}`,
        transition: 'all 0.2s ease',
        opacity: isOutOfStock ? 0.6 : 1,
        cursor: isOutOfStock ? 'not-allowed' : 'pointer',
        ...(animationsEnabled ? {
          animation: `fadeSlideUp 0.4s ease-out ${index * 0.03}s both`,
          '@keyframes fadeSlideUp': {
            from: { opacity: 0, transform: 'translateY(10px)' },
            to: { opacity: 1, transform: 'translateY(0)' },
          },
        } : {}),
        '&:hover': {
          borderColor: isOutOfStock ? posColors.error : posColors.accent,
          boxShadow: isOutOfStock ? 'none' : `0 8px 24px ${alpha(posColors.accent, 0.2)}`,
          transform: isOutOfStock ? 'none' : 'translateY(-2px)',
        },
      }}
    >
      {/* Product Image Placeholder */}
      <Box
        sx={{
          height: 100,
          backgroundColor: colors.surfaceLight,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        {product.images && product.images.length > 0 ? (
          <Box
            component="img"
            src={getImageUrl(product.images[0])}
            alt={product.name}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              filter: isOutOfStock ? 'grayscale(50%)' : 'none',
            }}
          />
        ) : (
          <LunchDiningIcon sx={{ fontSize: 40, color: colors.textMuted, opacity: 0.4 }} />
        )}

        {/* Out of Stock Badge */}
        {isOutOfStock && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              px: 1,
              py: 0.5,
              borderRadius: '6px',
              backgroundColor: posColors.error,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography
              sx={{
                fontSize: '10px',
                fontWeight: 700,
                color: '#FFF',
                textTransform: 'uppercase',
              }}
            >
              Out of Stock
            </Typography>
          </Box>
        )}

        {/* Quantity Badge */}
        {isInCart && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              width: 28,
              height: 28,
              borderRadius: '8px',
              backgroundColor: posColors.accent,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 700,
                color: '#FFF',
              }}
            >
              {quantity}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Product Info */}
      <Box sx={{ p: 1.5 }}>
        <Typography
          sx={{
            fontSize: '13px',
            fontWeight: 600,
            color: colors.textPrimary,
            mb: 0.5,
            lineHeight: 1.3,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            minHeight: 34,
          }}
        >
          {product.name}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
          <Box>
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 700,
                color: posColors.accent,
              }}
            >
              {formatPrice(product.price)}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography
                sx={{
                  fontSize: '10px',
                  fontWeight: 500,
                  color: availableStock <= 5 ? posColors.error : colors.textMuted,
                  fontFamily: 'monospace',
                }}
              >
                {availableStock} in stock
              </Typography>
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); onStockCheck(); }}
                sx={{
                  width: 18,
                  height: 18,
                  color: colors.textMuted,
                  '&:hover': {
                    color: posColors.accent,
                    backgroundColor: alpha(posColors.accent, 0.1),
                  },
                }}
              >
                <Inventory2Icon sx={{ fontSize: 12 }} />
              </IconButton>
            </Box>
          </Box>

          {isInCart ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); onDecrease(); }}
                sx={{
                  width: 28,
                  height: 28,
                  backgroundColor: alpha(posColors.error, 0.1),
                  color: posColors.error,
                  '&:hover': {
                    backgroundColor: alpha(posColors.error, 0.2),
                  },
                }}
              >
                <RemoveIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 700,
                  color: colors.textPrimary,
                  minWidth: 24,
                  textAlign: 'center',
                }}
              >
                {quantity}
              </Typography>
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); onIncrease(); }}
                sx={{
                  width: 28,
                  height: 28,
                  backgroundColor: alpha(posColors.success, 0.1),
                  color: posColors.success,
                  '&:hover': {
                    backgroundColor: alpha(posColors.success, 0.2),
                  },
                }}
              >
                <AddIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          ) : (
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); if (!isOutOfStock) onAdd(); }}
              disabled={isOutOfStock}
              sx={{
                width: 32,
                height: 32,
                backgroundColor: isOutOfStock ? colors.border : posColors.accent,
                color: isOutOfStock ? colors.textMuted : '#FFF',
                cursor: isOutOfStock ? 'not-allowed' : 'pointer',
                '&:hover': {
                  backgroundColor: isOutOfStock ? colors.border : posColors.accentLight,
                  transform: isOutOfStock ? 'none' : 'scale(1.1)',
                },
                '&.Mui-disabled': {
                  backgroundColor: colors.border,
                  color: colors.textMuted,
                },
                transition: 'all 0.2s ease',
              }}
            >
              <AddIcon sx={{ fontSize: 18 }} />
            </IconButton>
          )}
        </Box>
      </Box>
    </Box>
  );
};

// Order Item Component
interface OrderItemProps {
  item: CartItem;
  index: number;
  onRemove: () => void;
  onIncrease: () => void;
  onDecrease: () => void;
  formatPrice: (price: number) => string;
  colors: any;
  animationsEnabled: boolean;
}

const OrderItem: React.FC<OrderItemProps> = ({
  item,
  index,
  onRemove,
  onIncrease,
  onDecrease,
  formatPrice,
  colors,
  animationsEnabled,
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        p: 1.5,
        backgroundColor: colors.surfaceLight,
        borderRadius: '12px',
        position: 'relative',
        ...(animationsEnabled ? {
          animation: 'slideInLeft 0.3s ease-out',
          '@keyframes slideInLeft': {
            from: { opacity: 0, transform: 'translateX(-10px)' },
            to: { opacity: 1, transform: 'translateX(0)' },
          },
        } : {}),
      }}
    >
      {/* Order Number */}
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: '8px',
          backgroundColor: alpha(posColors.accent, 0.15),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{
            fontSize: '12px',
            fontWeight: 700,
            color: posColors.accent,
          }}
        >
          {String(index + 1).padStart(2, '0')}
        </Typography>
      </Box>

      {/* Item Details */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: '13px',
            fontWeight: 600,
            color: colors.textPrimary,
            lineHeight: 1.3,
            mb: 0.5,
          }}
        >
          {item.product.name}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Quantity Controls */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton
              size="small"
              onClick={onDecrease}
              sx={{
                width: 22,
                height: 22,
                padding: 0,
                backgroundColor: alpha(colors.textMuted, 0.1),
                color: colors.textSecondary,
                '&:hover': {
                  backgroundColor: alpha(posColors.error, 0.15),
                  color: posColors.error,
                },
              }}
            >
              <RemoveIcon sx={{ fontSize: 14 }} />
            </IconButton>
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 700,
                color: colors.textPrimary,
                minWidth: 20,
                textAlign: 'center',
              }}
            >
              {item.quantity}
            </Typography>
            <IconButton
              size="small"
              onClick={onIncrease}
              sx={{
                width: 22,
                height: 22,
                padding: 0,
                backgroundColor: alpha(colors.textMuted, 0.1),
                color: colors.textSecondary,
                '&:hover': {
                  backgroundColor: alpha(posColors.success, 0.15),
                  color: posColors.success,
                },
              }}
            >
              <AddIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>

          <Typography
            sx={{
              fontSize: '11px',
              color: colors.textMuted,
            }}
          >
            × {formatPrice(item.price)}
          </Typography>
        </Box>
      </Box>

      {/* Item Total */}
      <Typography
        sx={{
          fontSize: '14px',
          fontWeight: 700,
          color: colors.textPrimary,
          flexShrink: 0,
        }}
      >
        {formatPrice(item.price * item.quantity)}
      </Typography>

      {/* Remove Button */}
      <IconButton
        size="small"
        onClick={onRemove}
        sx={{
          position: 'absolute',
          top: 4,
          right: 4,
          width: 20,
          height: 20,
          padding: 0,
          color: colors.textMuted,
          opacity: 0,
          transition: 'all 0.2s ease',
          '.MuiBox-root:hover &': {
            opacity: 1,
          },
          '&:hover': {
            color: posColors.error,
            backgroundColor: alpha(posColors.error, 0.1),
          },
        }}
      >
        <CloseIcon sx={{ fontSize: 12 }} />
      </IconButton>
    </Box>
  );
};
